import { Fragment, useContext, useEffect } from "react";
import * as PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  ListSubheader,
  Divider,
  useTheme,
  CircularProgress,
} from "@mui/material";
import _ from "lodash";
import RefreshIcon from "@mui/icons-material/Refresh";

import { accessControl } from "utils";
import { AppContext } from "context";
import { useCompList } from "./hooks";
import ComponentCard from "./ComponentCard";

const compTypeName = {
  fm: "Firmware",
  ptn: "Pattern",
  vmImg: "VM Image",
  document: "Document",
};

const ComponentList = ({ compType }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    compList,
    compCategoryList,
    compFilters,
    loading,
    selectCompFilter,
    selectedFilters,
    resetFilters,
  } = useCompList(compType);
  const { userInfo } = useContext(AppContext);

  useEffect(() => {
    const roleList = _.get(userInfo, "roleList", []);
    if (
      !accessControl.isInternal(roleList) &&
      !accessControl.isPartner(roleList) &&
      !_.get(userInfo, "hasLicense", false)
    ) {
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box sx={{ mt: theme.spacing(2), mb: theme.spacing(5) }}>
      <Box
        sx={{
          marginTop: theme.spacing(3),
          marginLeft: theme.spacing(1),
          display: "flex",
          marginBottom: theme.spacing(3),
          "&& > *": {
            marginRight: theme.spacing(1),
          },
        }}
        noValidate
        autoComplete="off"
      >
        <TextField
          select
          label="Product Line"
          variant="outlined"
          style={{ minWidth: 150 }}
          size="small"
          value={selectedFilters.productLine}
          onChange={(e) => selectCompFilter("productLine", e.target.value)}
        >
          <MenuItem value="all">All</MenuItem>
          {compFilters.productLineList.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Product SKU"
          variant="outlined"
          style={{ minWidth: 150 }}
          size="small"
          value={selectedFilters.productSku}
          onChange={(e) => selectCompFilter("productSku", e.target.value)}
        >
          <MenuItem value="all">All</MenuItem>
          {compFilters.productSkuList.reduce((result, skuInfo) => {
            result.push(
              <ListSubheader
                key={skuInfo.name}
                disableSticky={true}
                style={{
                  lineHeight: "20px",
                  marginTop: "15px",
                  userSelect: "none",
                }}
              >
                {skuInfo.name}
              </ListSubheader>
            );
            _.get(skuInfo, "items", []).forEach((skuName) => {
              result.push(
                <MenuItem key={skuName} value={skuName}>
                  {skuName}
                </MenuItem>
              );
            });
            return result;
          }, [])}
        </TextField>
        {compType === "document" ? null : (
          <TextField
            select
            label="Component Type"
            variant="outlined"
            style={{ minWidth: 180 }}
            size="small"
            value={selectedFilters.compTypeCategory}
            onChange={(e) =>
              selectCompFilter("compTypeCategory", e.target.value)
            }
          >
            <MenuItem value="all">All</MenuItem>
            {compFilters.componentTypeList.map((compType) => (
              <MenuItem key={compType} value={compType}>
                {compType}
              </MenuItem>
            ))}
          </TextField>
        )}
        <Button onClick={resetFilters} startIcon={<RefreshIcon />}>
          Reset Filters
        </Button>
      </Box>
      <Box>
        {compList.length ? (
          Object.keys(compCategoryList).map((key) => {
            if (compCategoryList[key].length) {
              return (
                <Fragment key={key}>
                  <Typography
                    variant="h5"
                    component="div"
                    sx={{
                      display: "flex",
                      alignItems: "end",
                      marginTop: theme.spacing(4),
                      marginLeft: theme.spacing(1),
                    }}
                  >
                    <span>{compTypeName[key]}</span>
                    <Divider
                      sx={{
                        flexGrow: 1,
                        marginBottom: theme.spacing(1),
                        marginLeft: theme.spacing(1),
                        backgroundColor: "#241540",
                        opacity: 0.6,
                      }}
                    />
                  </Typography>
                  <Grid container spacing={2}>
                    {compCategoryList[key].map((compInfo, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <ComponentCard compInfo={compInfo} />
                      </Grid>
                    ))}
                  </Grid>
                </Fragment>
              );
            }
            return null;
          })
        ) : (
          <Box
            sx={{
              padding: theme.spacing(2),
            }}
          >
            <Typography>No Data</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
ComponentList.propTypes = {
  compType: PropTypes.string.isRequired,
};
export default ComponentList;
